import SEOHead from "@/components/SEOHead";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TID } from "@/constants/testIds";

const FAQS = [
  {
    q: "Who is Colin Darke?",
    a: "Colin Darke is a Detroit-based American artist, writer and attorney. He is known internationally as \"The Ambidextrous Artist\" for his ability to draw and paint with both hands simultaneously.",
  },
  {
    q: "What is Colin Darke known for?",
    a: "Drawing and painting with both hands simultaneously — including creating different images with each hand at the same time. His work has reached hundreds of millions of views across digital platforms.",
  },
  {
    q: "Can Colin Darke really draw with both hands at the same time?",
    a: "Yes. He can create mirrored images with both hands moving in tandem, or use each hand independently to render two entirely different subjects at once — with no mirrors and no duplicated video.",
  },
  {
    q: "Can Colin Darke draw two different pictures simultaneously?",
    a: "Yes. His hands can work independently — one may be sketching a portrait while the other is drawing a landscape.",
  },
  {
    q: "Where is Colin Darke from?",
    a: "Colin is based in the Detroit metropolitan area in Michigan, United States.",
  },
  {
    q: "Where has Colin Darke's artwork been featured?",
    a: "His work has been featured by Good Morning America / ABC News, Ripley's Believe It or Not! (including inclusion in the annual book Out of the Box!), Henry Ford Health, The Detroit News, Art21, and other national and international media.",
  },
  {
    q: "Was Colin Darke featured on Good Morning America?",
    a: "Yes — Good Morning America introduced a national television audience to Colin Darke's ambidextrous drawing process in October 2021.",
  },
  {
    q: "Was Colin Darke featured by Ripley's Believe It or Not?",
    a: "Yes — Colin's simultaneous ambidextrous artwork has been featured by Ripley's Believe It or Not!, including inclusion in the annual book Out of the Box!",
  },
  {
    q: "How many people have watched Colin Darke's artwork?",
    a: "Colin's ambidextrous drawing process has generated hundreds of millions of views across digital platforms, with individual videos reaching more than 10 million views.",
  },
  {
    q: "What kind of art does Colin Darke create?",
    a: "Colin's work moves between drawing, painting, portraiture, abstraction and experimentation, often expressed through his ambidextrous technique. Earlier gallery work was described by The Detroit News as employing a 'bold, colorful style reminiscent of the German Expressionists.'",
  },
];

export default function About() {
  return (
    <>
      <SEOHead
        title="Colin Darke Biography | The Ambidextrous Artist"
        description="Colin Darke is a Detroit-based American artist known as The Ambidextrous Artist. Read the full biography, philosophy, and answers to frequently asked questions."
        path="/about"
      />

      {/* HEADER */}
      <section className="pt-16 pb-8 border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">Biography</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
            Colin Darke
          </h1>
          <p className="mt-4 font-serif text-2xl text-[hsl(var(--ink-muted))] italic">
            The Ambidextrous Artist
          </p>
        </div>
      </section>

      {/* BIO NARRATIVE */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[900px] px-4 sm:px-8 lg:px-16 space-y-8 text-lg leading-relaxed text-[hsl(var(--ink))]">
          <p className="drop-cap">
            <span className="float-left font-serif text-8xl leading-[0.85] pr-3 pt-2 text-[hsl(var(--vermillion))]">
              C
            </span>
            olin Darke has been making art for more than four decades. Long
            before millions of people watched his hands move simultaneously
            across a page, Darke was drawing, painting, studying art and
            participating in Detroit's art community — and, whenever
            possible, challenging himself to make the process itself part
            of the piece.
          </p>
          <p>
            Then he began filming something he had always been able to do
            naturally: create with both hands. What looked ordinary to him
            looked almost impossible to everyone else.
          </p>
          <p>
            Darke can draw with either hand independently. He can use both
            hands simultaneously to construct opposite sides of a single
            image. More unusually, his hands can work independently — allowing
            him to create two different images at the same time.
          </p>
          <p>
            When he began sharing videos of the process, they spread around
            the world. Individual videos accumulated millions of views. His
            audience grew into the hundreds of thousands, and his work
            ultimately generated hundreds of millions of views across
            platforms.
          </p>
          <p>
            <em>Good Morning America</em> introduced a national television
            audience to his unusual creative process.{" "}
            <em>Ripley's Believe It or Not!</em> featured Darke and included
            him in its annual book, <em>Out of the Box!</em> His work has
            been shared by major recording artists and entertainers and has
            reached audiences far beyond the traditional gallery system.
          </p>
          <p className="font-serif text-2xl sm:text-3xl leading-snug py-6 border-y border-[hsl(var(--line))]">
            Yet the two-handed technique is a{" "}
            <em className="text-[hsl(var(--vermillion))]">process</em>, not
            the point of the work.
          </p>
          <p>
            At the center of Darke's practice is curiosity: what happens when
            an artist stops deciding that creativity has to look a particular
            way? His work moves between drawing, painting, portraiture,
            abstraction and experimentation. Sometimes the two hands
            cooperate. Sometimes they mirror one another. Sometimes they
            compete. Sometimes each appears to follow an entirely separate
            thought. That tension — control and spontaneity, symmetry and
            difference, left and right, discipline and play — has become an
            essential part of the work.
          </p>
        </div>
      </section>

      {/* ARTIST BEFORE VIRALITY */}
      <section className="py-16 sm:py-24 bg-[hsl(var(--bg-secondary))] border-y border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_ambidextrous-art/artifacts/x6zdlblz_artshowcandid.jpg"
              alt="A visitor viewing three of Colin Darke's paintings at a gallery show — a mixed-media geometric work, a seated elder with an owl in flames, and a figure on black."
              className="w-full object-cover"
            />
            <p className="mt-3 catalog-label">
              Detroit gallery exhibition · earlier career
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg text-[hsl(var(--ink))] leading-relaxed">
            <p className="catalog-label">Before the videos</p>
            <h2 className="font-serif text-4xl leading-tight">
              An artist before virality.
            </h2>
            <p>
              Colin is an artist, writer and attorney based in the Detroit
              area, and has participated in the Detroit art community for
              many years. His earlier exhibitions, curatorial activity and
              writing about art predate the ambidextrous videos by well over
              a decade.
            </p>
            <p>
              He grew up in an artmaking family — his sister,{" "}
              <em>Kelly Darke</em>, is also a working artist, and the two
              still exhibit alongside one another.
            </p>
            <p>
              <em>The Detroit News</em> once described his work as employing
              a{" "}
              <span className="text-[hsl(var(--vermillion))] italic">
                "bold, colorful style reminiscent of the German
                Expressionists."
              </span>
            </p>
            <p>
              His writing about Detroit's art community has appeared in
              publications including <em>Art21</em>. Ambidextrous drawing
              did not create the artist; it gave a worldwide audience an
              unusual window into an artistic practice that had existed for
              decades.
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[900px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">Philosophy</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
            "We are all artists."
          </h2>
          <div className="mt-8 space-y-5 text-lg text-[hsl(var(--ink))] leading-relaxed">
            <p>
              Colin believes creativity is inclusive rather than reserved for
              a small group of people who have been designated "artists."
              Creativity is something people can develop — by creating
              regularly, experimenting with mediums, and allowing themselves
              to make things without excessive self-editing.
            </p>
            <p>
              It's a stance built into the work itself: two hands, refusing
              to agree on which one is the "correct" one to use.
            </p>
          </div>
        </div>
      </section>

      {/* CANONICAL FACT BLOCK (LLM-friendly) */}
      <section className="py-16 bg-[hsl(var(--ink))] text-[hsl(var(--bg))]">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-8 lg:px-16 grid sm:grid-cols-2 gap-x-10 gap-y-6">
          <FactLine label="Full name" value="Colin Darke" />
          <FactLine label="Also known as" value="The Ambidextrous Artist" />
          <FactLine label="Nationality" value="American" />
          <FactLine label="Based in" value="Detroit metropolitan area, Michigan, USA" />
          <FactLine
            label="Known for"
            value="Drawing and painting with both hands simultaneously — including creating different images with each hand at the same time"
          />
          <FactLine
            label="Featured by"
            value="Good Morning America, Ripley's Believe It or Not!, Henry Ford Health, The Detroit News, Art21"
          />
          <FactLine
            label="Audience"
            value="Hundreds of millions of views across digital platforms"
          />
          <FactLine label="Also" value="Writer and attorney" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[900px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">FAQ</p>
          <h2 className="font-serif text-4xl sm:text-5xl mb-8">
            Common questions
          </h2>
          <Accordion
            type="single"
            collapsible
            className="w-full"
            data-testid={TID.faqAccordion}
          >
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-[hsl(var(--line))]"
              >
                <AccordionTrigger
                  className="font-serif text-xl sm:text-2xl text-left hover:text-[hsl(var(--vermillion))] hover:no-underline py-5"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-[hsl(var(--ink-muted))]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FAQ Page JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </>
  );
}

function FactLine({ label, value }) {
  return (
    <div className="flex gap-6 py-2 border-b border-[hsl(var(--bg))]/10">
      <span className="catalog-label text-[hsl(var(--bg))]/50 min-w-[110px]">
        {label}
      </span>
      <span className="text-[hsl(var(--bg))]/95">{value}</span>
    </div>
  );
}
